<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="totalRows"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="engineOnDuration_label"
            :label="$t('report.fields.totalEngineOnDuration')"
            label-for="engineOnDuration"
          >
            <span v-if="!isBusy">
              {{ summaryEngineOnDuration ?? '-' }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('startLocation') || columns.includes('stopLocation')"
      reportKey="summary-engine"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(vehicle)="data">
        {{ data.value.licensePlate ? data.value.licensePlate : '-' }}
      </template>

      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? `${data.item.vehicle.vehicleType.manufacturer} /
                    ${data.item.vehicle.vehicleType.model} /
                    ${data.item.vehicle.vehicleType.type}`
              : '-'
            : '-'
        }}
      </template>

      <template #cell(hullNo)="data">
        {{ data.item.vehicle.hullNo ? data.item.vehicle.hullNo : '-' }}
      </template>

      <template #cell(driver)="data">
        {{ data.value.fullname ? data.value.fullname : '-' }}
      </template>

      <template #cell(startTime)="data">
        {{ data.value ? momentDateShortMonth(data.value) : '-' }}<br />
        <span class="text-muted">{{
          data.value !== null ? momentTimeSeconds(data.value, true) : '-'
        }}</span>
      </template>

      <template #cell(stopTime)="data">
        {{ data.value ? momentDateShortMonth(data.value) : '-' }}<br />
        <span class="text-muted">{{
          data.value !== null ? momentTimeSeconds(data.value, true) : '-'
        }}</span>
      </template>

      <template #cell(engineOnDuration)="data">
        {{ data.value ? data.value : '-' }}
      </template>

      <template #cell(startLocation)="data">
        <b-skeleton width="70%" v-if="data.value.displayName == ''" />
        <AddressValue
          v-else
          :id="`startLocation-${data.index}`"
          :latitude="data.value.latitude"
          :longitude="data.value.longitude"
          :address="data.value.displayName"
        />
      </template>

      <template #cell(stopLocation)="data">
        <b-skeleton width="70%" v-if="data.value.displayName == ''" />
        <AddressValue
          v-else
          :id="`stopLocation-${data.index}`"
          :latitude="data.value.latitude"
          :longitude="data.value.longitude"
          :address="data.value.displayName"
        />
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { GET_SUMMARY_ENGINE, SUMARY_ENGINE_REPORT } from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import AddressValue from '../../components/AddressValue.vue'
import DownloadReportDropdown from '../../components/DownloadReportDropdown.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    AddressValue,
    DownloadReportDropdown
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      summaryEngineOnDuration: null,
      reportKey: 'summary-engine',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
        const model = {
          startDate,
          endDate,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            GET_SUMMARY_ENGINE,
            {
              filter: {
                ...model,
                column: this.columns,
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.summaryEngine.summaryEngine.data
          const isGetStartLoc = this.columns.includes('startLocation')
          const isGetStopLoc = this.columns.includes('stopLocation')
          result.forEach((item, index) => {
            result[index].startLocation.displayName = isGetStartLoc ? '' : '-'
            result[index].stopLocation.displayName = isGetStopLoc ? '' : '-'
          })
          self.summaryEngineOnDuration = res.summaryEngine.summaryEngine.summary.engineOnDuration
          self.totalRows = res.summaryEngine.summaryEngine.pagination.total
          self.isBusy = false
          self.message = res.summaryEngine.message
          self.isError = !res.summaryEngine.isSuccessful
          self.modalVerifReport += 1
          return res.summaryEngine.summaryEngine
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    itemDownloadDropdown() {
      return [
        {
          name: 'excel',
          label: this.$t('global.action.downloadExcel'),
          onClick: () => {
            this.downloadReport('xlsx')
          },
          icon: this.excelIcon,
        },
        {
          name: 'pdf',
          label: this.$t('global.action.downloadPdf'),
          onClick: () => {
            this.downloadReport('pdf')
          },
          icon: this.pdfIcon,
        },
      ]
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.summaryEngineReportBackground.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: SUMARY_ENGINE_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.summaryEngineReportBackground)
        })
    },
  },
}
</script>
